import { useFlag } from '@unleash/proxy-client-react';
import { Avatar, ActionIcon, Center, NavLink, Paper, SimpleGrid, Text, UnstyledButton } from '@mantine/core';
import { BrokerNerdsBarkerCoreEnumsMarketplace } from '../../api';
import mobileClasses from '../../pages/MarketFilters.Mobile.module.css';
import classes from './SeatingChart.MarketplaceSelectorNew.module.css';
import { ReactNode, useState } from 'react';
import { MarketplaceSelectorProps } from './SeatingChart.MarketplaceSelector.types';
import LinkIcon from '../../components/icons/Link';
import SyncAltIcon from '../../components/icons/SyncAlt';
import { useSeatingChart } from './SeatingChart.hooks';
import { useClickOutside } from '@mantine/hooks';
import { getMarketplaceLogo } from '../../img/marketPlaceImage';

export function MarketplaceSelectorNew({ onChangeMarketplace, selectedMarketplaceId, availableMarketplaces, onClosePopover }: MarketplaceSelectorProps) {
  const isTicketNetworkMarketplaceEnabled = useFlag('ticketnetwork-marketplace');
  const isTicomboMarketplaceEnabled = useFlag('ticombo-marketplace');
  const { openMarketplaceLinks, setIsMarketSelectorOpen } = useSeatingChart('openMarketplaceLinks', 'setIsMarketSelectorOpen');

  const toggle = document.querySelector('[data-market-selector-toggle]') as HTMLElement;
  const [marketplaceSelectorRef, setMarketplaceSelectorRef] = useState<HTMLElement | null>(null);
  useClickOutside(() => onClosePopover && onClosePopover(), null, [marketplaceSelectorRef, toggle]);

  //   mostly the same as MarketplaceSelectorMobile, added a Container with a text header, and a link footer
  //   can this be refactored to extract just the SimpleGrid so it can be used on the Mobile component?

  return (
    <Paper ref={setMarketplaceSelectorRef} className={classes.MarketplaceSelector} p={16} shadow="md" pb={10}>
      <Text size="sm" fw={600} w="100%">
        Select a Marketplace
      </Text>
      <SimpleGrid cols={5} my="md" mb={10} px={0} spacing="xs" style={{ borderBottom: '1px solid var(--colors-gray-0)' }} pb={20}>
        <MarketButton
          title="TEVO"
          selected={selectedMarketplaceId === BrokerNerdsBarkerCoreEnumsMarketplace.TicketEvolution}
          img={getMarketplaceLogo(BrokerNerdsBarkerCoreEnumsMarketplace.TicketEvolution)}
          disabled={!availableMarketplaces?.includes(BrokerNerdsBarkerCoreEnumsMarketplace.TicketEvolution)}
          disabledMessage="Not Available"
          onClick={
            availableMarketplaces?.includes(BrokerNerdsBarkerCoreEnumsMarketplace.TicketEvolution)
              ? () => onChangeMarketplace(BrokerNerdsBarkerCoreEnumsMarketplace.TicketEvolution)
              : undefined
          }
        />
        <MarketButton
          title="SeatGeek"
          selected={selectedMarketplaceId === BrokerNerdsBarkerCoreEnumsMarketplace.SeatGeek}
          img={getMarketplaceLogo(BrokerNerdsBarkerCoreEnumsMarketplace.SeatGeek)}
          disabled={!availableMarketplaces?.includes(BrokerNerdsBarkerCoreEnumsMarketplace.SeatGeek)}
          disabledMessage="Not Available"
          onClick={
            availableMarketplaces?.includes(BrokerNerdsBarkerCoreEnumsMarketplace.SeatGeek) ? () => onChangeMarketplace(BrokerNerdsBarkerCoreEnumsMarketplace.SeatGeek) : undefined
          }
        />
        <MarketButton
          title="Ticketmaster"
          selected={selectedMarketplaceId === BrokerNerdsBarkerCoreEnumsMarketplace.Ticketmaster}
          img={getMarketplaceLogo(BrokerNerdsBarkerCoreEnumsMarketplace.Ticketmaster)}
          disabled={!availableMarketplaces?.includes(BrokerNerdsBarkerCoreEnumsMarketplace.Ticketmaster)}
          disabledMessage="Not Available"
          onClick={
            availableMarketplaces?.includes(BrokerNerdsBarkerCoreEnumsMarketplace.Ticketmaster)
              ? () => onChangeMarketplace(BrokerNerdsBarkerCoreEnumsMarketplace.Ticketmaster)
              : undefined
          }
        />
        <MarketButton
          title="TicketNetwork"
          selected={selectedMarketplaceId === BrokerNerdsBarkerCoreEnumsMarketplace.TicketNetwork}
          img={getMarketplaceLogo(BrokerNerdsBarkerCoreEnumsMarketplace.TicketNetwork)}
          disabled={!isTicketNetworkMarketplaceEnabled || !availableMarketplaces?.includes(BrokerNerdsBarkerCoreEnumsMarketplace.TicketNetwork)}
          disabledMessage={!isTicketNetworkMarketplaceEnabled ? 'Coming Soon' : 'Not Available'}
          onClick={
            availableMarketplaces?.includes(BrokerNerdsBarkerCoreEnumsMarketplace.TicketNetwork)
              ? () => onChangeMarketplace(BrokerNerdsBarkerCoreEnumsMarketplace.TicketNetwork)
              : undefined
          }
        />
        <MarketButton
          title="Ticombo"
          selected={selectedMarketplaceId === BrokerNerdsBarkerCoreEnumsMarketplace.Ticombo}
          img={getMarketplaceLogo(BrokerNerdsBarkerCoreEnumsMarketplace.Ticombo)}
          disabled={!isTicomboMarketplaceEnabled || !availableMarketplaces?.includes(BrokerNerdsBarkerCoreEnumsMarketplace.Ticombo)}
          disabledMessage={!isTicomboMarketplaceEnabled ? 'Coming Soon' : 'Not Available'}
          onClick={
            availableMarketplaces?.includes(BrokerNerdsBarkerCoreEnumsMarketplace.Ticombo) ? () => onChangeMarketplace(BrokerNerdsBarkerCoreEnumsMarketplace.Ticombo) : undefined
          }
        />
      </SimpleGrid>
      <NavLink
        className={classes.link}
        href="#"
        onClick={() => {
          openMarketplaceLinks();
          setIsMarketSelectorOpen(false);
        }}
        label="Marketplace Links"
        leftSection={
          <span style={{ transform: 'rotate(-45deg)', display: 'flex' }}>
            <LinkIcon size={20} />
          </span>
        }
        px={6}
        py={0}
      />
    </Paper>
  );
}

type MarketButtonProps = {
  title: string;
  selected?: boolean;
  img?: string | ReactNode;
  disabled?: boolean;
  disabledMessage?: string;
  onClick?: () => void;
};

export function MarketButton({ title, selected, img, disabled, disabledMessage, onClick }: MarketButtonProps) {
  return (
    <>
      {!disabled ? (
        <UnstyledButton
          h={96}
          py="xs"
          style={
            selected
              ? { border: '1px solid var(--colors-selectedBorder)', borderRadius: 6, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }
              : { border: '1px solid var(--colors-gray-2)', borderRadius: 6, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }
          }
          bg={selected ? 'var(--colors-selectedBg)' : 'var(--colors-paper)'}
          onClick={onClick}
          className={classes.marketButton}
        >
          <ActionIcon component="div" bg="var(--colors-brandcolor-3)" radius="xl" h={44} w={44} mt={2} style={{ border: '2px solid var(--colors-brandcolor-6)' }}>
            <SyncAltIcon color="var(--colors-brandcolor-6)" size={20} />
          </ActionIcon>
          <Avatar
            key={title.toLowerCase()}
            m={0}
            mt={2}
            radius="xl"
            h={44}
            w={44}
            variant="outline"
            className={mobileClasses.avatar}
            bg="var(--colors-paper)"
            color={selected ? 'var(--colors-selectedBorder)' : 'var(--colors-gray-5)'}
            styles={
              selected
                ? {
                    placeholder: {
                      borderColor: 'var(--colors-selectedBorder)',
                    },
                  }
                : {}
            }
            style={selected ? { borderColor: 'var(--colors-selectedBorder)' } : {}}
          >
            {typeof img === 'string' ? <img className="mantine-Avatar-image" height={44} width={44} src={img} alt={title} /> : img}
          </Avatar>
          <Text fz={10} truncate maw="100%" px={2}>
            {title}
          </Text>
        </UnstyledButton>
      ) : (
        <UnstyledButton
          h={96}
          py="xs"
          pos="relative"
          style={{ border: '1px solid var(--colors-gray-2)', filter: 'grayscale(1)', borderRadius: 6, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}
        >
          <Center h={44} w={44} pos="absolute" mt={2} fz={8} lts={0.4} lh={1.3} style={{ textTransform: 'uppercase', textAlign: 'center', zIndex: 1 }}>
            {disabledMessage}
          </Center>
          <Avatar
            key={title.toLowerCase()}
            m={0}
            mt={2}
            radius="xl"
            h={44}
            w={44}
            variant="outline"
            opacity={0.25}
            className={mobileClasses.avatar}
            color="var(--colors-darkPaper)"
          >
            {typeof img === 'string' ? <img className="mantine-Avatar-image" height={44} width={44} src={img} alt={title} /> : img}
          </Avatar>
          <Text opacity={0.5} fz={10}>
            {title}
          </Text>
        </UnstyledButton>
      )}
    </>
  );
}
